/**
 * One scan, written out in full: the verdict on top, the evidence under it.
 *
 * Lifted out of `SiteScannerSection` the day a second surface needed the same
 * report - the call list (the Vihjed tab) opens it in a dialog. An operator
 * working that list should be reading the SAME page they would get by typing
 * the domain into the scanner, not a smaller summary of it that drifts a little
 * further from the real thing with every change to one of the two.
 *
 * Everything here is presentation. The scan arrives as a prop and refetching is
 * the caller's business: the scanner page rescans through its own hook, the
 * dialog through its own request.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import type { SiteScan } from "../../../lib/siteScan";
import { FindingsList } from "./FindingsList";
import { ScanHero } from "./ScanHero";
import { ScanRawDialog } from "./ScanRawDialog";
import { TechGrid } from "./TechGrid";
import {
  CompanyPanel,
  ContactsPanel,
  DomainPanel,
  FreshnessPanel,
  ProfilePanel,
  SeoPanel,
  TransportPanel,
  WordPressPanel,
} from "./panels";
import type { T } from "./types";

export function ScanDetail({
  scan,
  refreshing,
  onRefresh,
  showRaw = true,
  t,
}: {
  scan: SiteScan;
  refreshing: boolean;
  onRefresh: () => void;
  /**
   * The raw-material dialog is a modal, so a caller who is already inside one
   * turns it off rather than stacking two: one Escape would close both, and the
   * page behind the dialog offers the same button.
   */
  showRaw?: boolean;
  t: T;
}) {
  const [rawOpen, setRawOpen] = useState(false);

  return (
    <motion.div
      // Keyed on the scan, so switching to another domain replays the entrance
      // instead of swapping the numbers under a card that never moved.
      key={`${scan.domain}-${scan.checkedAt}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      // `crm-scan` is not decoration: it is what the phone rules in index.css
      // hang the report's narrower card gutters on. The report is the one place
      // in the CRM where cards can be three paddings deep.
      className="crm-scan space-y-4"
    >
      <ScanHero
        scan={scan}
        refreshing={refreshing}
        onRefresh={onRefresh}
        onRaw={showRaw ? () => setRawOpen(true) : undefined}
        t={t}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <FindingsList findings={scan.findings} t={t} />
        <div className="space-y-4">
          {/* Directly under the findings, because it is the answer to the
              question the findings raise: yes, but does any of it cost the
              owner anything. */}
          {scan.profile && (
            <ProfilePanel
              profile={scan.profile}
              defectScore={scan.defectScore}
              opportunityScore={scan.opportunityScore}
              t={t}
            />
          )}
          {scan.identity && <CompanyPanel identity={scan.identity} domain={scan.domain} t={t} />}
          {scan.contacts && (
            <ContactsPanel
              contacts={scan.contacts}
              companyName={scan.identity?.companyName ?? undefined}
              regCode={scan.identity?.register?.regCode}
              domain={scan.domain}
              t={t}
            />
          )}
        </div>
      </div>

      {scan.freshness && <FreshnessPanel freshness={scan.freshness} t={t} />}
      {scan.wordpress && <WordPressPanel wp={scan.wordpress} freshness={scan.freshness?.wp ?? null} t={t} />}

      <TechGrid technologies={scan.technologies} t={t} />

      <div className="grid gap-4 lg:grid-cols-2">
        {scan.seo && <SeoPanel seo={scan.seo} t={t} />}
        <TransportPanel http={scan.http} t={t} />
      </div>

      {/* Who the domain belongs to and who serves it. Keyed on the domain so
          switching to another scan starts a fresh lookup instead of leaving the
          previous company's nameservers on screen. */}
      <DomainPanel key={scan.domain} domain={scan.domain} t={t} />

      {showRaw ? <ScanRawDialog scan={scan} open={rawOpen} onOpenChange={setRawOpen} t={t} /> : null}
    </motion.div>
  );
}
